import { useCallback } from "react";
import apiClient from "../api/apiClient";
import API_CONFIG from "../config/api";
import useBusinessResource from "../hooks/useBusinessResource";

const positiveId = (value) => Number.isSafeInteger(Number(value)) && Number(value) > 0;
const selectable = (row) => !row.deleted_at && !row.archived_at &&
  !["inactive", "disabled", "deleted", "archived", "0"].includes(String(row.status ?? "active").toLowerCase());

// Existing authenticated endpoints own authorisation. Keep only the fields needed
// by this form in its cache, and exhaust the paginated reference lists.
export async function loadProductFormOptions(companyId, signal) {
  if (!positiveId(companyId)) throw new Error("Sélectionnez une entreprise avant d’ouvrir le formulaire.");
  const config = { signal, headers: { "X-Company-Id": String(companyId) }, params: { company_id: companyId } };
  const readPages = async (path, key) => {
    const rows = [];
    for (let page = 1; ; page += 1) {
      const response = await apiClient.get(`${API_CONFIG.BASE_URL}/api/${path}`, {
        ...config, params: { ...config.params, page, limit: 100 },
      });
      if (!Array.isArray(response.data?.[key])) throw new Error("Les choix du formulaire sont indisponibles.");
      rows.push(...response.data[key]);
      const lastPage = Number(response.data.pagination?.totalPages ?? 1);
      if (page >= lastPage) return rows;
    }
  };
  const [categoriesResponse, units, warehouses, taxes] = await Promise.all([
    apiClient.get(`${API_CONFIG.BASE_URL}/api/categories`, config),
    readPages("units", "units"), readPages("warehouses", "warehouses"), readPages("taxes", "taxes"),
  ]);
  if (!Array.isArray(categoriesResponse.data)) throw new Error("Les catégories sont indisponibles.");
  const scoped = (rows) => {
    if (rows.some((row) => String(row.company_id) !== String(companyId))) {
      throw new Error("Le contexte des choix a changé. Rechargez le formulaire.");
    }
    return rows.filter(selectable);
  };
  return {
    categories: scoped(categoriesResponse.data).map(({ id, name, company_id }) => ({ id, name, company_id })),
    units: scoped(units).map(({ id, name, short_name, company_id }) => ({ id, name, short_name, company_id })),
    warehouses: scoped(warehouses).map(({ id, name, company_id }) => ({ id, name, company_id })),
    taxes: scoped(taxes).map(({ id, name, rate, company_id }) => ({ id, name, rate, company_id })),
  };
}

export function getProductReferenceConflicts(values, options) {
  if (!options) return [];
  return [
    ["warehouse", "warehouses", "L’établissement sélectionné n’est plus disponible."],
    ["id_categorie", "categories", "La catégorie sélectionnée n’est plus disponible. Choisissez une autre catégorie."],
    ["id_unit", "units", "L’unité sélectionnée n’est plus disponible. Vérifiez aussi les conditionnements."],
    ["taxe", "taxes", "La taxe sélectionnée n’est plus disponible. Choisissez une autre taxe ou aucune taxe."],
  ].filter(([field, key]) => positiveId(values[field]) &&
    !options[key].some((row) => String(row.id) === String(values[field])))
    .map(([field, , message]) => ({ field, message }));
}

export default function useProductFormOptions({ userId, companyId, warehouseId, open }) {
  const load = useCallback((signal) => loadProductFormOptions(companyId, signal), [companyId]);
  return useBusinessResource({ resource: "product-form-options", userId, companyId, warehouseId,
    moduleScope: "commercial", scopeLevel: "company", enabled: open, active: open, load });
}
